/**
 * API layer for TikTok product management endpoints.
 * Calls `zaviago_backend.storefront.tiktok_product.*` whitelisted methods.
 */

import { getERPClient } from "@/lib/erp/client";
import { uploadFile } from "@/lib/erp/files";

const API = "zaviago_backend.storefront.tiktok_product";

/** Image object tracked in Zustand store and sent to backend */
export interface TikTokImage {
  uri: string;    // TikTok CDN URI (used in Partial Edit API)
  url: string;    // Display URL (first CDN mirror)
  width: number;
  height: number;
}

export interface UploadImageResponse {
  uri: string;
  url: string;
  width: number;
  height: number;
}

export interface SaveChangesResponse {
  status: "success" | "noop";
  save_type: "instant_only" | "audited_only" | "mixed" | "none";
  message?: string;
  results?: {
    instant: Array<{ type: string; result: any }>;
    audited: Array<{ type: string; result: any }>;
  };
}

export interface ProductFreshResponse {
  [key: string]: any;
}

// ── Category & Attribute types ────────────────────────────────────

export interface TikTokCategory {
  id: string;
  parent_id: string;
  local_name: string;
  is_leaf: boolean;
  permission_statuses: string[];
}

export interface TikTokBrand {
  id: string;
  name: string;
  is_t1_brand: boolean;
  authorized_status: string;
}

export interface TikTokAttribute {
  id: string;
  name: string;
  type: "PRODUCT_ATTRIBUTE" | "SALES_ATTRIBUTE";
  is_required: boolean;
  is_customizable: boolean;
  values: Array<{ id: string; name: string }>;
}

const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_TYPES = ["image/jpeg", "image/png"];

/** Validate image file before upload to TikTok CDN */
export function validateTikTokImage(file: File): string | null {
  if (!ALLOWED_TYPES.includes(file.type)) {
    return "Only JPEG and PNG images are supported";
  }
  if (file.size > MAX_IMAGE_SIZE) {
    return "Image must be under 5MB";
  }
  return null;
}

export const tiktokProductApi = {
  /**
   * Smart save router — sends dirty fields to backend which routes to
   * the correct TikTok APIs (Partial Edit / Price Update / Inventory Update).
   */
  saveChanges(
    connectionName: string,
    productId: string,
    changes: Record<string, any>,
  ) {
    return getERPClient().call<SaveChangesResponse>(`${API}.save_changes`, {
      connection_name: connectionName,
      product_id: productId,
      changes: JSON.stringify(changes),
    });
  },

  /**
   * Upload an image to TikTok CDN via backend proxy.
   * Step 1: Upload file to Frappe file system.
   * Step 2: Backend reads file and forwards to TikTok CDN.
   */
  async uploadImage(connectionName: string, file: File): Promise<TikTokImage> {
    // Step 1: Upload file to Frappe file system
    const { file_url } = await uploadFile(file, {
      isPrivate: false,
      folder: "Home",
    });

    // Step 2: Forward to TikTok CDN via backend
    const result = await getERPClient().call<UploadImageResponse>(
      `${API}.upload_image`,
      { connection_name: connectionName, file_url },
    );

    return result;
  },

  /**
   * Fetch all TikTok product categories (flat list — build tree client-side).
   * Thai locale is used by default on the backend.
   */
  getCategories(connectionName: string) {
    return getERPClient().call<TikTokCategory[]>(`${API}.get_categories`, {
      connection_name: connectionName,
    });
  },

  /**
   * Fetch product attributes for a leaf category.
   * Returns both PRODUCT_ATTRIBUTE and SALES_ATTRIBUTE — filter client-side.
   */
  getCategoryAttributes(connectionName: string, categoryId: string) {
    return getERPClient().call<TikTokAttribute[]>(
      `${API}.get_category_attributes`,
      {
        connection_name: connectionName,
        category_id: categoryId,
      },
    );
  },

  /**
   * Search brands for a category. Debounce on frontend (300ms).
   */
  getBrands(connectionName: string, params: { category_id?: string; brand_name?: string }) {
    return getERPClient().call<{ brands: TikTokBrand[] }>(`${API}.get_brands`, {
      connection_name: connectionName,
      category_id: params.category_id,
      brand_name: params.brand_name,
    });
  },

  /** Activate a product on TikTok (SELLER_DEACTIVATED/DRAFT → ACTIVATE) */
  activateProduct(connectionName: string, productId: string) {
    return getERPClient().call<{ status: string }>(`${API}.activate_product`, {
      connection_name: connectionName,
      product_id: productId,
    });
  },

  /** Deactivate a product on TikTok (ACTIVATE → SELLER_DEACTIVATED) */
  deactivateProduct(connectionName: string, productId: string) {
    return getERPClient().call<{ status: string }>(`${API}.deactivate_product`, {
      connection_name: connectionName,
      product_id: productId,
    });
  },

  /** Permanently delete a product from TikTok (irreversible) */
  deleteProduct(connectionName: string, productId: string) {
    return getERPClient().call<{ status: string }>(`${API}.delete_product`, {
      connection_name: connectionName,
      product_id: productId,
    });
  },

  /** Create a new product on TikTok Shop */
  createProduct(connectionName: string, productData: Record<string, any>) {
    return getERPClient().call<{
      product_id: string;
      listing_name: string;
      status: string;
    }>(`${API}.create_product`, {
      connection_name: connectionName,
      product_data: JSON.stringify(productData),
    });
  },

  /** AI-powered category suggestion based on product title */
  recommendCategory(connectionName: string, productTitle: string) {
    return getERPClient().call<{
      categories: Array<{
        category_id: string;
        category_name: string;
      }>;
    }>(`${API}.recommend_category`, {
      connection_name: connectionName,
      product_title: productTitle,
    });
  },

  /**
   * Force re-fetch product data from TikTok API and update local listing.
   */
  getProductFresh(connectionName: string, productId: string) {
    return getERPClient().call<ProductFreshResponse>(
      `${API}.get_product_fresh`,
      {
        connection_name: connectionName,
        product_id: productId,
      },
    );
  },
};
